import { useCallback, useEffect, useMemo, useRef } from 'react';

type TrackingData = {
  elementName?: string;
  linkUrl?: string;
  campaignName?: string;
};

const trackAction = (
  eventAction: string,
  spotName?: string,
  elementName?: string,
  linkUrl?: string,
  campaignName?: string
) => {
  if (typeof window === 'undefined') return;

  const windowWithDataLayer = window as Window & { dataLayer?: unknown[] };
  if (!windowWithDataLayer.dataLayer) {
    windowWithDataLayer.dataLayer = [];
  }
  const dataLayer = windowWithDataLayer.dataLayer;

  const dataObject = {
    event: 'wt-interaction',
    eventAction,
    ...(spotName && { spot_name: spotName }),
    ...(elementName && { element_name: elementName }),
    ...(linkUrl && { link_url: linkUrl }),
    ...(campaignName && { campaign_name: campaignName }),
  };

  dataLayer.push(dataObject);
};

const extractDataFromTarget = (target: HTMLElement): TrackingData => {
  const linkUrl = (target as HTMLAnchorElement).href || target.getAttribute('href') || undefined;
  const elementName = target.getAttribute('data-wt-name') || undefined;
  const campaignName = target.getAttribute('data-wt-campaign') || undefined;

  return {
    elementName,
    linkUrl,
    campaignName,
  };
};

/**
 * Custom hook to track spot visibility (display) and click interactions using IntersectionObserver.
 *
 * @param spotName - The name of the spot to track
 * @param options - Optional IntersectionObserver options (defaults to { threshold: 0.5 })
 * @returns A ref to attach to the spot container element
 *
 * @example
 * ```tsx
 * const spotRef = useSpotTracking(spotTrackingName, { threshold: 0.2 });
 * return <div ref={spotRef}>...</div>
 * ```
 */
export const useSpotTracking = (spotName?: string, options?: IntersectionObserverInit) => {
  const ref = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const observerOptions = useMemo(() => ({ threshold: 0.5, ...options }), [options]);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('[data-wt-name]') as HTMLElement | null;

      if (!target) return;

      const { elementName, linkUrl, campaignName } = extractDataFromTarget(target);

      trackAction('click', spotName, elementName, linkUrl, campaignName);
    },
    [spotName]
  );

  useEffect(() => {
    const element = ref.current;
    if (!spotName || !element) return;

    element.addEventListener('click', handleClick, true);

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          trackAction('display', spotName);
          observerRef.current?.disconnect();
        }
      });
    }, observerOptions);

    observerRef.current.observe(element);

    return () => {
      element.removeEventListener('click', handleClick, true);
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [spotName, handleClick, observerOptions]);

  return ref;
};
